import {
  BacktesterConfigSummary,
  BacktesterParamsPayload,
  BacktesterRunRequest,
  DeleteBacktesterConfigParams,
  DeleteBacktesterConfigRequest,
  FetchBacktesterConfigParams,
  FetchBacktesterConfigRequest,
  FetchBacktesterConfigResult,
  ListBacktesterConfigsParams,
  ListBacktesterConfigsRequest,
  ListBacktesterConfigsWireResult,
  SaveBacktesterConfigParams,
  SaveBacktesterConfigRequest,
} from './backtester-config-api.types';

const isBacktesterParamsPayload = (value: unknown): value is BacktesterParamsPayload => {
  if (!isRecord(value)) return false;
  return (
    isRecord(value.market_data) &&
    Array.isArray(value.stratlets) &&
    typeof (value.market_data as { t_0?: unknown }).t_0 === 'string' &&
    typeof (value.market_data as { t_1?: unknown }).t_1 === 'string' &&
    typeof (value.market_data as { frequency?: unknown }).frequency === 'number'
  );
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeBacktesterConfigSummary = (
  value: Record<string, unknown>,
): BacktesterConfigSummary | null => {
  if (typeof value.name !== 'string' || !value.name.trim()) {
    return null;
  }

  return {
    name: value.name,
    ...(typeof value.created_at === 'string' ? { created_at: value.created_at } : {}),
    ...(typeof value.updated_at === 'string' ? { updated_at: value.updated_at } : {}),
  };
};

const parseBacktesterConfigListItem = (
  item: string | BacktesterConfigSummary,
): BacktesterConfigSummary | null => {
  if (typeof item === 'string') {
    try {
      const parsed: unknown = JSON.parse(item);
      if (isRecord(parsed)) {
        return normalizeBacktesterConfigSummary(parsed);
      }
      return null;
    } catch {
      return null;
    }
  }

  if (isRecord(item)) {
    return normalizeBacktesterConfigSummary(item);
  }

  return null;
};

export const parseBacktesterConfigListResult = (
  result: ListBacktesterConfigsWireResult | undefined,
): BacktesterConfigSummary[] => {
  if (!result?.length) {
    return [];
  }
  return result
    .map(parseBacktesterConfigListItem)
    .filter((x): x is BacktesterConfigSummary => x != null);
};

export const parseFetchBacktesterConfigResult = (
  raw: FetchBacktesterConfigResult | string | undefined,
): FetchBacktesterConfigResult | null => {
  if (raw == null) {
    return null;
  }

  let data: unknown = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return null;
    }
  }

  if (!isRecord(data) || typeof data.name !== 'string' || !data.name.trim()) {
    return null;
  }

  if (!isRecord(data.config) || !isBacktesterParamsPayload(data.config)) {
    return null;
  }

  return {
    name: data.name,
    created_at: typeof data.created_at === 'string' ? data.created_at : '',
    updated_at: typeof data.updated_at === 'string' ? data.updated_at : '',
    config: data.config,
  };
};

export const BACKTESTER_CONFIG_KEYS = {
  RUN: 'BACKTESTER_RUN',
  LIST: 'BACKTESTER_CONFIG_LIST',
  FETCH: 'BACKTESTER_CONFIG_FETCH',
  SAVE: 'BACKTESTER_CONFIG_SAVE',
  DELETE: 'BACKTESTER_CONFIG_DELETE',
} as const;

// `id` defaults to the shared full-study run key. Per-variant runs pass a
// distinct id (RUN + strategy id) so concurrent single-variant runs don't
// clobber each other's response handler — the WS layer keys handlers by id.
export const buildRunBacktestRequest = (
  params: BacktesterParamsPayload,
  id: string = BACKTESTER_CONFIG_KEYS.RUN,
): BacktesterRunRequest => ({
  jsonrpc: '2.0',
  id,
  method: 'backtester',
  params,
});

export const buildListConfigsRequest = (
  params: ListBacktesterConfigsParams,
): ListBacktesterConfigsRequest => ({
  jsonrpc: '2.0',
  id: BACKTESTER_CONFIG_KEYS.LIST,
  method: 'list_backtester_configs',
  params,
});

export const buildFetchConfigRequest = (
  params: FetchBacktesterConfigParams,
): FetchBacktesterConfigRequest => ({
  jsonrpc: '2.0',
  id: BACKTESTER_CONFIG_KEYS.FETCH,
  method: 'fetch_backtester_config',
  params,
});

export const buildSaveConfigRequest = (
  params: SaveBacktesterConfigParams,
): SaveBacktesterConfigRequest => ({
  jsonrpc: '2.0',
  id: BACKTESTER_CONFIG_KEYS.SAVE,
  method: 'save_backtester_config',
  params,
});

export const buildDeleteConfigRequest = (
  params: DeleteBacktesterConfigParams,
): DeleteBacktesterConfigRequest => ({
  jsonrpc: '2.0',
  id: BACKTESTER_CONFIG_KEYS.DELETE,
  method: 'delete_backtester_config',
  params,
});

export const BACKTESTER_TEST_CONFIG: BacktesterParamsPayload = {
  market_data: {
    t_0: '2024-01-01 00:00:00',
    t_1: new Date().toISOString(),
    frequency: 60,
  },
  stratlets: [
    {
      name: 'Test config 1',
      account: {
        token: 'BTC',
        token_balance: 1.0,
        usd_balance: 0.0,
        perpetual: 0.0,
      },
      execution: {
        perp_spread: -0.0001,
        vol_spread: 0.005,
        perp_fee: 0.0003,
        options_fee: 0.00025,
        execution_style: 'AVERAGE_IN',
        execution_time: '15',
        notional_scaling: true,
        notional_scaling_type: 'ABSOLUTE',
        options_settle_in_usd: false,
        weekdays: 'MON,TUE,WED,THU,FRI',
      },
      hedging: { frequency: 1 },
      portfolio: [],
      reporting: {
        frequency: 1440,
        exclude_midnight: false,
        risk_report: false,
        blotter_report: false,
      },
    },
    {
      name: 'Conservative — BTC options overlay',
      account: {
        token: 'BTC',
        token_balance: 0.5,
        usd_balance: 50_000,
        perpetual: 0.1,
      },
      execution: {
        perp_spread: -0.00005,
        vol_spread: 0.003,
        perp_fee: 0.00025,
        options_fee: 0.0002,
        execution_style: 'AVERAGE_IN',
        execution_time: '09',
        notional_scaling: true,
        notional_scaling_type: 'ABSOLUTE',
        options_settle_in_usd: true,
        weekdays: 'MON,TUE,WED,THU,FRI',
      },
      hedging: {
        frequency: 4,
        include_funding_rates: true,
        delta_threshold: 0.05,
        delta_threshold_upper: null,
        delta_threshold_lower: null,
        delta_target: null,
        include_token_balance_in_delta: true,
      },
      portfolio: [
        { tenor: '1W', delta: '25P', notional: 25_000 },
        { tenor: '2W', delta: '10P', notional: 15_000 },
      ],
      reporting: {
        frequency: 1440,
        exclude_midnight: false,
        risk_report: false,
        blotter_report: false,
      },
    },
    {
      name: 'Aggressive — ETH weekend',
      account: {
        token: 'ETH',
        token_balance: 25,
        usd_balance: 0,
        perpetual: 8,
      },
      execution: {
        perp_spread: -0.0002,
        vol_spread: 0.012,
        perp_fee: 0.00045,
        options_fee: 0.00035,
        execution_style: 'AVERAGE_IN',
        execution_time: '16',
        notional_scaling: false,
        notional_scaling_type: 'ABSOLUTE',
        options_settle_in_usd: false,
        weekdays: 'MON,TUE,WED,THU,FRI,SAT,SUN',
      },
      hedging: {
        frequency: 1,
        include_funding_rates: false,
        delta_threshold: null,
        delta_threshold_upper: 0.15,
        delta_threshold_lower: -0.15,
        delta_target: 0,
        include_token_balance_in_delta: false,
      },
      portfolio: [
        { tenor: '1W', delta: '50C', notional: 100_000 },
        { tenor: '1M', strike: '3500', notional: 40_000 },
      ],
      reporting: {
        frequency: 1440,
        exclude_midnight: false,
        risk_report: false,
        blotter_report: false,
      },
    },
  ],
};
